import React, { Component } from 'react';
import Radium from 'radium';
import { shape, bool, arrayOf } from 'prop-types';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { submit, formValueSelector } from 'redux-form';
import { push } from 'connected-react-router';
import { UTLabel, AlertHandler } from '@widergy/energy-ui';

import { actionCreators as authActions } from 'redux/authHandlers';
import { message } from 'utils/stringUtils';
import { userInfoPropTypes, phonePropTypes } from 'types/userTypes';
import { CHANGE_USER_INFO } from 'constants/reduxForms';
import { ROUTES } from 'constants/routes';
import { appConfig } from 'config/customization';
import { gaTrackPageView } from 'services/AnalyticsService';

import { hasValues } from './utils';
import ChangeUserInfo from './layout';
import styles from './styles';

const retrieveDefaultPhone = phoneList => {
  const defaultPhone = phoneList.find(phone => phone.default);
  return defaultPhone ? defaultPhone.number : null;
};

const mapDefaultValue = (phoneList, defaultNumber) => {
  const phones = [...phoneList];
  let index = phones.findIndex(element => element.default);
  if (index >= 0) phones[index] = { ...phones[index], default: false };
  index = phones.findIndex(element => element.number === defaultNumber);
  if (index >= 0) phones[index] = { ...phones[index], default: true };
  return phones;
};

class ChangeUserInfoContainer extends Component {
  state = { defaultPhoneNumber: null };

  componentDidMount = () => {
    this.props.dispatch(authActions.getUserProfile());
    gaTrackPageView(ROUTES.CHANGE_USER_INFO);
  };

  componentDidUpdate = prevProps => {
    if (prevProps.updateUserProfileLoading && !this.props.updateUserProfileLoading)
      this.props.dispatch(authActions.getUserProfile());
    if (prevProps.userInfo !== this.props.userInfo && this.props.userInfo) {
      const defaultPhone = retrieveDefaultPhone(
        this.props.userInfo.phones.concat(this.props.userInfo.mobiles)
      );
      if (defaultPhone) this.handleSetDefaultPhone(defaultPhone);
    }
    if (prevProps.phones !== this.props.phones && this.props.phones && this.state.defaultPhoneNumber) {
      const defaultPhone = this.props.phones.find(phone => phone.number === this.state.defaultPhoneNumber);
      // eslint-disable-next-line react/no-did-update-set-state
      if (!defaultPhone) this.setState({ defaultPhoneNumber: null });
    }
  };

  componentWillUnmount() {
    if (this.props.currentUser.updated_profile === false && this.props.currentUser.is_customer) {
      AlertHandler.info(message('ChangeUserInfo:warningMessage'));
      this.props.dispatch(push(ROUTES.CHANGE_USER_INFO));
    }
  }

  handleSubmitForm = () => this.props.dispatch(submit(CHANGE_USER_INFO));

  handleGoToDashboard = () => this.props.dispatch(push(ROUTES.ACCOUNT_STATUS));

  handleSubmit = values => {
    if (
      appConfig.userDataManagement.phoneRequired &&
      !hasValues(values.phones) &&
      !hasValues(values.mobiles)
    ) {
      AlertHandler.error(message('Configuration:phoneIsRequired'));
    } else if (appConfig.userDataManagement.defaultPhoneRequired && !this.state.defaultPhoneNumber) {
      AlertHandler.error(message('Configuration:defaultPhoneRequired'));
    } else {
      const userInformation = {
        phones: mapDefaultValue(values.phones, this.state.defaultPhoneNumber),
        emails: values.emails,
        mobiles: mapDefaultValue(values.mobiles, this.state.defaultPhoneNumber)
      };
      this.props.dispatch(authActions.updateUserProfile(userInformation, values.password));
    }
  };

  handleSetDefaultPhone = defaultPhoneNumber =>
    this.setState({
      defaultPhoneNumber
    });

  render() {
    return (
      <div style={styles.container}>
        <UTLabel bold large>
          {message('ChangeUserInfo:title')}
        </UTLabel>
        <ChangeUserInfo
          initialValues={this.props.userInfo}
          loading={this.props.userProfileLoading || this.props.updateUserProfileLoading}
          onSetDefaultPhone={this.handleSetDefaultPhone}
          defaultPhoneNumber={this.state.defaultPhoneNumber}
          onSubmitForm={this.handleSubmitForm}
          onSubmit={this.handleSubmit}
          onGoToDashboard={this.handleGoToDashboard}
        />
      </div>
    );
  }
}

ChangeUserInfoContainer.propTypes = {
  userInfo: shape(userInfoPropTypes),
  userProfileLoading: bool,
  updateUserProfileLoading: bool,
  phones: arrayOf(shape(phonePropTypes)),
  currentUser: userInfoPropTypes
};

const formSelector = formValueSelector(CHANGE_USER_INFO);

const userInformationSelector = createSelector(
  store => store.auth.currentUser.additional_information.phones,
  store => store.auth.currentUser.additional_information.mobiles,
  store => store.auth.currentUser.additional_information.emails,
  (phones, mobiles, emails) => ({ phones, mobiles, emails })
);

const phonesSelector = createSelector(
  store => formSelector(store, 'phones'),
  store => formSelector(store, 'mobiles'),
  (phones, mobiles) => (phones ? phones.concat(mobiles) : mobiles)
);

const mapStateToProps = store => ({
  userInfo: userInformationSelector(store),
  userProfileLoading: store.auth.userProfileLoading,
  updateUserProfileLoading: store.auth.updateUserProfileLoading,
  phones: phonesSelector(store),
  currentUser: store.auth.currentUser
});

export default connect(mapStateToProps)(Radium(ChangeUserInfoContainer));
